import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import DateSelector from '../../DateSelector';
import TypeSelector from '../../TypeSelector';
import { SUBSCRIPTION_TYPES } from '../../../constants/subscriptionTypes';
import CustomButton from '../../CustomButton';
import Spinner from '../../Spinner';
import { getPremiumTransactions } from '../../../redux/actions/analytics';
import { formatDate } from '../helper';

const PremiumTransactions = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [selectedType, setSelectedType] = useState(SUBSCRIPTION_TYPES.ALL);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [excludeTestUsers, setExcludeTestUsers] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [developerMode, setDeveloperMode] = useState(false);
  const [keyBuffer, setKeyBuffer] = useState('');

  const transactions = useSelector((state) => state.analytics.premium_transactions);
  const summary = useSelector((state) => state.analytics.premium_transactions_summary);
  const loading = useSelector((state) => state.analytics.premium_transactions_loading);

  const search = useCallback(() => {
    dispatch(
      getPremiumTransactions(
        formatDate(startDate),
        formatDate(endDate),
        selectedType,
        excludeTestUsers,
        showEmail
      )
    );
    setIsFormChanged(false);
  }, [dispatch, startDate, endDate, selectedType, excludeTestUsers, showEmail]);

  useEffect(() => {
    search();
  }, [search]);

  // Easter egg: Listen for "developer" being typed
  useEffect(() => {
    const handleKeyPress = (e) => {
      const newBuffer = (keyBuffer + e.key).toLowerCase().slice(-9); // Keep last 9 characters
      setKeyBuffer(newBuffer);

      if (newBuffer === 'developer') {
        setDeveloperMode(true);
      }
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [keyBuffer]);

  const headers = showEmail
    ? [
        { label: 'Import ID', key: 'importId' },
        { label: 'Source', key: 'source' },
        { label: 'Email', key: 'email' },
        { label: 'Type', key: 'type' },
        { label: 'Reason', key: 'reason' },
        { label: 'Created At', key: 'createdAt' },
      ]
    : [
        { label: 'Import ID', key: 'importId' },
        { label: 'Source', key: 'source' },
        { label: 'Type', key: 'type' },
        { label: 'Reason', key: 'reason' },
        { label: 'Created At', key: 'createdAt' },
      ];

  /**
   * Render a single category summary table
   */
  const renderCategorySummary = (title, metrics) => {
    return (
      <div className="overflow-x-auto w-full px-8 mb-8" key={title}>
        <h3 className="text-lg font-bold mb-2">{title}</h3>
        <table className="min-w-full bg-white shadow-md rounded">
          <thead className="bg-gray-100">
            <tr>
              {metrics.map((metric, index) => (
                <th key={index} className="px-4 py-2 text-center">
                  {metric.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {metrics.map((metric, index) => (
                <td key={index} className="px-4 py-2 text-center border-b">
                  {metric.value || 0}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Render summary table based on subscription type
   */
  const renderSummary = () => {
    if (!summary || Object.keys(summary).length === 0) {
      return null;
    }

    // When ALL is selected, show all category summaries
    if (selectedType === SUBSCRIPTION_TYPES.ALL || selectedType === 'ALL') {
      const categories = [];

      // TD (Teladoc)
      if (summary.newTdSubscriptions !== undefined) {
        const tdTotal = (summary.newTdSubscriptions || 0) + (summary.renewedTdSubscriptions || 0);
        categories.push(
          renderCategorySummary('TD (Teladoc)', [
            { label: 'All new transactions', value: tdTotal },
            { label: 'New TD subscriptions', value: summary.newTdSubscriptions },
            { label: 'Renewed TD subscriptions', value: summary.renewedTdSubscriptions },
            { label: 'Cancelled TD subscriptions', value: summary.cancelledTdSubscriptions },
          ])
        );
      }

      // Tanfolyamok (Courses)
      if (
        summary.newBetfPurchases !== undefined ||
        summary.newSzkttfPurchases !== undefined ||
        summary.newBgtPurchases !== undefined ||
        summary.newRhPurchases !== undefined
      ) {
        const coursesTotal =
          (summary.newBetfPurchases || 0) +
          (summary.newSzkttfPurchases || 0) +
          (summary.newBgtPurchases || 0) +
          (summary.newRhPurchases || 0);
        categories.push(
          renderCategorySummary('Tanfolyamok', [
            { label: 'All new transactions', value: coursesTotal },
            { label: 'New BETF purchases', value: summary.newBetfPurchases },
            { label: 'New BGT purchases', value: summary.newBgtPurchases },
            { label: 'New RH purchases', value: summary.newRhPurchases },
            { label: 'New SZKTTF purchases', value: summary.newSzkttfPurchases },
          ])
        );
      }

      // Utónévkereső (Name Finder)
      if (summary.newUk1Purchases !== undefined || summary.newUk8Purchases !== undefined) {
        const nameFinderTotal = (summary.newUk1Purchases || 0) + (summary.newUk8Purchases || 0);
        categories.push(
          renderCategorySummary('Utónévkereső', [
            { label: 'All new transactions', value: nameFinderTotal },
            { label: 'New UK1 purchases', value: summary.newUk1Purchases },
            { label: 'New UK8 purchases', value: summary.newUk8Purchases },
          ])
        );
      }

      // Premium V1
      if (summary.newPremiumV1Subscriptions !== undefined) {
        const premiumV1Total =
          (summary.newPremiumV1Subscriptions || 0) + (summary.renewedPremiumV1Subscriptions || 0);
        categories.push(
          renderCategorySummary('Premium V1', [
            { label: 'All new transactions', value: premiumV1Total },
            { label: 'New Premium V1 subscriptions', value: summary.newPremiumV1Subscriptions },
            {
              label: 'Renewed Premium V1 subscriptions',
              value: summary.renewedPremiumV1Subscriptions,
            },
            {
              label: 'Cancelled Premium V1 subscriptions',
              value: summary.cancelledPremiumV1Subscriptions,
            },
          ])
        );
      }

      return (
        <div className="w-full">
          <h2 className="text-xl font-bold mb-4 px-8">Summary</h2>
          {categories}
        </div>
      );
    }

    // Teladoc summary (only when TD is specifically selected)
    if (selectedType === SUBSCRIPTION_TYPES.TD) {
      const tdTotal = (summary.newTdSubscriptions || 0) + (summary.renewedTdSubscriptions || 0);
      return (
        <div className="w-full">
          <h2 className="text-xl font-bold mb-4 px-8">Summary</h2>
          {renderCategorySummary('TD (Teladoc)', [
            { label: 'All new transactions', value: tdTotal },
            { label: 'New TD subscriptions', value: summary.newTdSubscriptions },
            { label: 'Renewed TD subscriptions', value: summary.renewedTdSubscriptions },
            { label: 'Cancelled TD subscriptions', value: summary.cancelledTdSubscriptions },
          ])}
        </div>
      );
    }

    // Courses summary (category filter)
    if (selectedType === SUBSCRIPTION_TYPES.COURSES) {
      const coursesTotal =
        (summary.newBetfPurchases || 0) +
        (summary.newSzkttfPurchases || 0) +
        (summary.newBgtPurchases || 0) +
        (summary.newRhPurchases || 0);
      return (
        <div className="w-full">
          <h2 className="text-xl font-bold mb-4 px-8">Summary</h2>
          {renderCategorySummary('Tanfolyamok', [
            { label: 'All new transactions', value: coursesTotal },
            { label: 'New BETF purchases', value: summary.newBetfPurchases },
            { label: 'New BGT purchases', value: summary.newBgtPurchases },
            { label: 'New RH purchases', value: summary.newRhPurchases },
            { label: 'New SZKTTF purchases', value: summary.newSzkttfPurchases },
          ])}
        </div>
      );
    }

    // Name Finder summary (category filter)
    if (selectedType === SUBSCRIPTION_TYPES.NAME_FINDER) {
      const nameFinderTotal = (summary.newUk1Purchases || 0) + (summary.newUk8Purchases || 0);
      return (
        <div className="w-full">
          <h2 className="text-xl font-bold mb-4 px-8">Summary</h2>
          {renderCategorySummary('Utónévkereső', [
            { label: 'All new transactions', value: nameFinderTotal },
            { label: 'New UK1 purchases', value: summary.newUk1Purchases },
            { label: 'New UK8 purchases', value: summary.newUk8Purchases },
          ])}
        </div>
      );
    }

    // Premium V1 summary
    if (selectedType === SUBSCRIPTION_TYPES.PREMIUM_V1) {
      const premiumV1Total =
        (summary.newPremiumV1Subscriptions || 0) + (summary.renewedPremiumV1Subscriptions || 0);
      return (
        <div className="w-full">
          <h2 className="text-xl font-bold mb-4 px-8">Summary</h2>
          {renderCategorySummary('Premium V1', [
            { label: 'All new transactions', value: premiumV1Total },
            { label: 'New Premium V1 subscriptions', value: summary.newPremiumV1Subscriptions },
            {
              label: 'Renewed Premium V1 subscriptions',
              value: summary.renewedPremiumV1Subscriptions,
            },
            {
              label: 'Cancelled Premium V1 subscriptions',
              value: summary.cancelledPremiumV1Subscriptions,
            },
          ])}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="w-full flex flex-col items-center">
      <DateSelector
        startDate={startDate}
        endDate={endDate}
        startDateChanged={(date) => {
          setStartDate(date);
          setIsFormChanged(true);
        }}
        endDateChanged={(date) => {
          setEndDate(date);
          setIsFormChanged(true);
        }}
      />

      <TypeSelector
        value={selectedType}
        onChange={(type) => {
          setSelectedType(type);
          setIsFormChanged(true);
        }}
        required={false}
        label="Subscription Type"
        includePremiumV1={true}
      />

      {developerMode && (
        <div className="flex flex-col items-center gap-4 my-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="excludeTestUsers"
              checked={excludeTestUsers}
              onChange={(e) => {
                setExcludeTestUsers(e.target.checked);
                setIsFormChanged(true);
              }}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="excludeTestUsers" className="cursor-pointer">
              Exclude test users
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showEmail"
              checked={showEmail}
              onChange={(e) => {
                setShowEmail(e.target.checked);
                setIsFormChanged(true);
              }}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="showEmail" className="cursor-pointer">
              Show email addresses
            </label>
          </div>
        </div>
      )}

      <div className="my-8 flex items-center justify-center w-full">
        <CustomButton type="button" className="text-white" onClick={search}>
          Search
        </CustomButton>

        {transactions && transactions.length > 0 && !isFormChanged && (
          <CSVLink
            className="bg-orange text-white p-2 rounded ml-4"
            filename={`PremiumTransactions-${formatDate(startDate)}-${formatDate(endDate)}.csv`}
            data={transactions}
            headers={headers}
          >
            Download this table
          </CSVLink>
        )}
      </div>

      {loading && <Spinner />}

      {!loading && transactions && transactions.length > 0 && (
        <>
          {renderSummary()}

          <div className="overflow-x-auto w-full px-8">
            <h2 className="text-xl font-bold mb-4">Transaction Details</h2>
            <table className="min-w-full bg-white shadow-md rounded">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Import ID</th>
                  <th className="px-4 py-2 text-center">Source</th>
                  {showEmail && <th className="px-4 py-2 text-center">Email</th>}
                  <th className="px-4 py-2 text-center">Type</th>
                  <th className="px-4 py-2 text-center">Reason</th>
                  <th className="px-4 py-2 text-center">Created At</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-2">{transaction.importId}</td>
                    <td className="px-4 py-2 text-center">{transaction.source}</td>
                    {showEmail && <td className="px-4 py-2 text-center">{transaction.email}</td>}
                    <td className="px-4 py-2 text-center">{transaction.type}</td>
                    <td className="px-4 py-2 text-center">{transaction.reason}</td>
                    <td className="px-4 py-2 text-center">{transaction.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && (!transactions || transactions.length === 0) && (
        <div className="text-center mt-4">No transactions found for the selected period</div>
      )}
    </div>
  );
};

export default PremiumTransactions;
